import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type ProjectPhase = 'knowledge' | 'resourcing' | 'production' | 'personal'

interface ProjectPhaseState {
  // Current active phase
  currentPhase: ProjectPhase

  // User email for personal phase filtering
  userEmail: string | null

  // Pending blueprint to be added to project
  pendingBlueprint: any | null

  // Node visibility settings (which types of nodes to show in each phase)
  knowledgeNodeTypes: string[]
  resourcingNodeTypes: string[]
  productionNodeTypes: string[]
  personalNodeTypes: string[]

  // Actions
  setPhase: (phase: ProjectPhase) => void
  setUserEmail: (email: string) => void
  setPendingBlueprint: (blueprint: any | null) => void
  isNodeVisibleInCurrentPhase: (nodeType: string, nodeData: any) => boolean
}

export const useProjectPhaseStore = create<ProjectPhaseState>()(
  persist((set, get) => ({
    currentPhase: 'knowledge',
    userEmail: null,
    pendingBlueprint: null,    // Updated node type lists
    knowledgeNodeTypes: [
      'default',
      'nodedocument',
      'noderesearcher',
      'nodechat',
      'nodeintellica',
      'nodesmartnotes',
      'nodeformwizard',
      'commandnodechat',
      'nodedocumentdesigner',
      'nodediagrams',
      'nodediagramsmermaid',
      'nodeknowledgeextraction',
      'nodefileminer',
      'nodecomparefiles',
      'nodedocumenttemplate',
      'nodejavascript',
      'annotationnode',
      'nodequickstart',
      'nodequickstartdynamic',
      'nodeimagegenerator',
      'noderesource',
      'noderesourcepdf',
      'nodestrategy',
      'noderecommendator',
      'nodepresence',
      'nodeagency',
      'nodeglobalconstants',
      'nodeknowledgeset',
      'nodecomposite',
      'nodewebextract',
      'nodefacilitator',
      'noderesearcher',
      'nodewebsearch',
      'nodeagent',
      'nodedomainknowledge',
      'nodeaiknot',
      'nodehtmlfields',
      'nodegroupblueprint',
      'nodeworkflow',
      'groupnode',
      'group',
    ], resourcingNodeTypes: [
      'default',
      'nodefileminer',
      'nodecomparefiles',
      'annotationnode',
      'nodequickstartdynamic',
      'nodeimagegenerator',
      'noderesourcepdf',
      'nodestrategy',
      'noderecommendator',
      'nodepresence',
      'nodeagency',
      'nodeknowledgeset',
      'nodecomposite',
      'nodewebextract',
      'nodefacilitator',
      'noderesearcher',
      'nodewebsearch',
      'nodeglobalconstants',
      'nodeagent',
      'nodedomainknowledge',
      'nodehtmlfields',
      'nodeaiknot',
      'nodegroupblueprint',
      'nodeworkflow',
      'groupnode',
      'group',], productionNodeTypes: [
        'default',
        'nodedocumenttemplate',
        'nodejavascript',
        'annotationnode',
        'nodequickstartdynamic',
        'nodeimagegenerator',
        'nodestrategy',
        'noderecommendator',
        'nodepresence',
        'nodeagency',
        'nodeknowledgeset',
        'nodecomposite',
        'nodewebextract',
        'nodefacilitator',
        'noderesearcher',
        'nodewebsearch',
        'nodeglobalconstants',
        'nodeagent',
        'nodedomainknowledge',
        'nodehtmlfields',
        'nodeaiknot',
        'nodegroupblueprint',
        'nodeworkflow',
        'groupnode',
        'group',
      ], personalNodeTypes: [
        'default',
        'nodedocument',
        'noderesearcher',
        'nodechat',
        'intellicanode',
        'nodesmartnotes',
        'nodedocumentdesigner',
        'nodediagrams',
        'nodediagramsmermaid',
        'nodeknowledgeextraction',
        'nodefileminer',
        'nodecomparefiles',
        'nodedocumenttemplate',
        'nodejavascript',
        'annotationnode',
        'nodequickstartdynamic',
        'nodeimagegenerator',
        'nodestrategy',
        'noderecommendator',
        'nodepresence',
        'nodeagency',
        'nodeknowledgeset',
        'nodecomposite',
        'nodewebextract',
        'nodefacilitator',
        'noderesearcher',
        'nodewebsearch',
        'nodeglobalconstants',
        'nodeagent',
        'nodedomainknowledge',
        'nodehtmlfields',
        'nodeaiknot',
        'nodegroupblueprint',
        'nodeworkflow',
        'groupnode',
        'group',
      ], setPhase: (phase: ProjectPhase) => {
        set({ currentPhase: phase });
      },

    setUserEmail: (email: string) => {
      set({ userEmail: email });
    },

    setPendingBlueprint: (blueprint: any | null) => {
      set({ pendingBlueprint: blueprint });
    },

    isNodeVisibleInCurrentPhase: (nodeType: string, nodeData: any = {}) => {
      const state = get();
      switch (state.currentPhase) {
        case 'knowledge':
          return state.knowledgeNodeTypes.includes(nodeType);
        case 'resourcing':
          return state.resourcingNodeTypes.includes(nodeType);
        case 'production':
          return state.productionNodeTypes.includes(nodeType);
        case 'personal':
          if (!nodeData) return false;
          const createdBy =
            nodeData.createdBy ||
            nodeData.userEmail ||
            (nodeData.data && (nodeData.data.createdBy || nodeData.data.userEmail));
          return (
            state.personalNodeTypes.includes(nodeType) && createdBy === state.userEmail
          );
        default:
          return true;
      }
    },
  }),
    {
      name: 'spechops-project-phase',
      // Optionally, customize the merge strategy to prioritize code-defined lists
      merge: (persistedState, currentState) => {
        return {
          ...currentState, // Use the updated lists from code
          currentPhase: (persistedState as ProjectPhaseState).currentPhase, // Keep persisted phase
          userEmail: (persistedState as ProjectPhaseState).userEmail, // Keep persisted email
        };
      },
    }
  )
);